import React, { useEffect, useState } from 'react';
import { useCannedResponsesStore, CannedResponse } from '@/store/cannedResponsesStore';
import { Zap } from 'lucide-react';

interface Props {
  text: string;
  onSelect: (content: string) => void;
  onClose: () => void;
}

export const CannedResponsesAutocomplete: React.FC<Props> = ({ text, onSelect, onClose }) => {
  const { cannedResponses, fetchCannedResponses, fetched } = useCannedResponsesStore();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!fetched) {
      fetchCannedResponses();
    }
  }, [fetched, fetchCannedResponses]);

  const query = text.toLowerCase();
  const filtered = cannedResponses.filter(item => 
    item.shortcut.toLowerCase().startsWith(query) ||
    item.shortcut.toLowerCase().includes(query.replace('/', ''))
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        onSelect(filtered[selectedIndex].content);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  useEffect(() => {
    if (!text.startsWith('/') || filtered.length === 0) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filtered, selectedIndex, text]);

  if (!text.startsWith('/')) {
    return null;
  }

  if (filtered.length === 0) {
    return null;
  }

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-1">
      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1 border-b border-gray-100">
        <Zap className="w-3 h-3 text-amber-500" />
        Respostas Rápidas (Atalhos /)
      </div>
      <div className="py-1">
        {filtered.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.content)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex flex-col gap-0.5 ${
              idx === selectedIndex ? 'bg-primary-50 text-primary-900 font-medium' : 'hover:bg-gray-50 text-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded border border-primary-100">
                {item.shortcut}
              </span>
            </div>
            <p className="text-xs text-gray-500 line-clamp-1">{item.content}</p>
          </button>
        ))}
      </div>
    </div>
  );
};
